/**
 * Allowlist HTML sanitiser for agent-written content shown on public pages.
 *
 * The market update web view renders a template's HTML on the CRM origin, so a
 * <script>, an onerror= or a javascript: link in it would run there. This keeps
 * the formatting an email-style update uses and drops everything else: unknown
 * tags are unwrapped (their text stays), dangerous ones are removed with their
 * contents, event-handler attributes go, and links/images must be http(s),
 * mailto, tel, relative or an inline image.
 */

const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'caption', 'center', 'div', 'em', 'font', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'small', 'span', 'strong', 'sub',
  'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
]);

// Removed together with everything inside them.
const DROPPED_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea',
  'select', 'link', 'meta', 'base', 'frame', 'frameset', 'svg', 'math', 'template', 'noscript',
]);

const ALLOWED_ATTRS = new Set([
  'align', 'alt', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'color', 'colspan',
  'height', 'href', 'rel', 'rowspan', 'size', 'src', 'style', 'target', 'title', 'valign', 'width',
]);

const SAFE_URL = /^(?:https?:|mailto:|tel:|\/|#|data:image\/(?:png|jpe?g|gif|webp);base64,)/i;

const cleanStyle = (value) =>
  /expression\s*\(|javascript:|url\s*\(\s*['"]?\s*(?:javascript|vbscript|data:text)/i.test(value) ? '' : value;

const cleanNode = (node, doc) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === 8) { // comment
      child.remove();
      return;
    }
    if (child.nodeType !== 1) return; // text stays
    const tag = child.tagName.toLowerCase();
    if (DROPPED_TAGS.has(tag)) {
      child.remove();
      return;
    }
    cleanNode(child, doc);
    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep the (already cleaned) children, lose the element.
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      child.remove();
      return;
    }
    Array.from(child.attributes).forEach(({ name, value }) => {
      const attr = name.toLowerCase();
      if (!ALLOWED_ATTRS.has(attr)) {
        child.removeAttribute(name);
      } else if ((attr === 'href' || attr === 'src') && !SAFE_URL.test(value.trim().replace(/[\s\u0000-\u001f]+/g, ''))) {
        child.removeAttribute(name);
      } else if (attr === 'style') {
        const safe = cleanStyle(value);
        if (safe) child.setAttribute('style', safe); else child.removeAttribute('style');
      }
    });
    if (tag === 'a' && child.getAttribute('target') === '_blank') {
      child.setAttribute('rel', 'noopener noreferrer');
    }
  });
};

export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  cleanNode(doc.body, doc);
  return doc.body.innerHTML;
};

export default sanitizeHtml;
